import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Lock, Eye, Database, Mail, CreditCard, Users } from 'lucide-react';

export const PrivacyPolicy: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-mint/30 via-neutral-bg to-primary-mint/20">
      <header className="bg-white/80 backdrop-blur-sm border-b border-gray-200 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <img src="/logo.png" alt="FridgeLife" className="w-10 h-10" />
              <h1 className="text-xl font-bold text-primary-green">FridgeLife</h1>
            </div>
            <Link to="/" className="flex items-center gap-2 px-4 py-2 text-sm text-primary-green font-medium hover:bg-primary-mint/50 rounded-lg transition-all">
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-xl p-8 md:p-12">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-primary-mint rounded-full flex items-center justify-center">
              <Shield className="w-6 h-6 text-primary-green" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900">Privacy Policy</h1>
          </div>
          <p className="text-gray-600 mb-8">Last Updated: November 9, 2025</p>

          <div className="bg-primary-mint/50 rounded-lg p-6 mb-8">
            <p className="text-gray-700 leading-relaxed">
              At FridgeLife, we take your privacy seriously. This Privacy Policy explains how we collect, use, 
              disclose, and safeguard your information when you use our food inventory management service. 
              Please read this policy carefully to understand our practices regarding your personal data.
            </p>
          </div>

          <div className="prose prose-lg max-w-none">
            <section className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <Database className="w-6 h-6 text-primary-green" />
                <h2 className="text-2xl font-bold text-gray-900">1. Information We Collect</h2>
              </div>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">1.1 Information You Provide</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                When you create an account and use FridgeLife, we collect:
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4 mb-4">
                <li><strong>Account Information:</strong> Email address, password (encrypted), and display name</li>
                <li><strong>Food Inventory Data:</strong> Item names, categories, expiration dates, quantities, prices, and barcodes</li>
                <li><strong>Household Information:</strong> Household names and member email addresses</li>
                <li><strong>Usage Data:</strong> Features you use, pages you visit, and actions you take</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">1.2 Automatically Collected Information</h3>
              <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4 mb-4">
                <li><strong>Device Information:</strong> Browser type, operating system, and device identifiers</li>
                <li><strong>Log Data:</strong> IP address, access times, and pages viewed</li>
                <li><strong>Cookies:</strong> Session cookies for authentication and functionality</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">1.3 Barcode Scanning</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                When you use our barcode scanning feature:
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
                <li>We access your device camera (with your permission)</li>
                <li>We send barcode data to Open Food Facts API to retrieve product information</li>
                <li>We store scan history locally in your browser and count monthly scans in our database</li>
                <li>Camera access is only active when you explicitly use the scanning feature</li>
              </ul>
            </section>

            <section className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <Eye className="w-6 h-6 text-primary-green" />
                <h2 className="text-2xl font-bold text-gray-900">2. How We Use Your Information</h2>
              </div>
              <p className="text-gray-600 leading-relaxed mb-4">
                We use the information we collect to:
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
                <li><strong>Provide Our Service:</strong> Store and manage your food inventory data</li>
                <li><strong>Send Notifications:</strong> Email you about expiring items (2 days before expiration)</li>
                <li><strong>Process Payments:</strong> Handle subscription payments through Stripe</li>
                <li><strong>Improve Our Service:</strong> Analyze usage patterns to enhance features and user experience</li>
                <li><strong>Customer Support:</strong> Respond to your questions and resolve technical issues</li>
                <li><strong>Security:</strong> Detect and prevent fraud, abuse, and security incidents</li>
                <li><strong>Legal Compliance:</strong> Comply with applicable laws and regulations</li>
              </ul>
            </section>

            <section className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <Users className="w-6 h-6 text-primary-green" />
                <h2 className="text-2xl font-bold text-gray-900">3. Information Sharing</h2>
              </div>
              <p className="text-gray-600 leading-relaxed mb-4">
                We do not sell your personal information. We may share your information only in the following circumstances:
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">3.1 Household Members</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                When you invite members to your household, they can view and edit all items in your shared inventory. 
                All household members can see who added each item and when.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">3.2 Service Providers</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                We work with trusted third-party service providers to operate our service:
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4 mb-4">
                <li><strong>Firebase (Google):</strong> Hosting, authentication, and database services</li>
                <li><strong>Stripe:</strong> Payment processing and subscription management</li>
                <li><strong>SendGrid (Twilio):</strong> Email delivery for notifications and invitations</li>
                <li><strong>Open Food Facts:</strong> Product information from barcode scans</li>
              </ul>
              <p className="text-gray-600 leading-relaxed mb-4">
                These providers are contractually obligated to protect your data and use it only to provide services to us.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">3.3 Legal Requirements</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                We may disclose your information if required by law or in response to:
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
                <li>Valid legal processes (subpoenas, court orders)</li>
                <li>Requests from law enforcement or government agencies</li>
                <li>Protection of our rights, property, or safety</li>
                <li>Prevention of fraud or security threats</li>
              </ul>
            </section>

            <section className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <Lock className="w-6 h-6 text-primary-green" />
                <h2 className="text-2xl font-bold text-gray-900">4. Data Security</h2>
              </div>
              <p className="text-gray-600 leading-relaxed mb-4">
                We implement industry-standard security measures to protect your information:
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4 mb-4">
                <li><strong>Encryption:</strong> All data is encrypted in transit (HTTPS/TLS) and at rest</li>
                <li><strong>Authentication:</strong> Firebase Authentication with secure password hashing</li>
                <li><strong>Access Controls:</strong> Firestore security rules enforce data access permissions</li>
                <li><strong>Monitoring:</strong> Continuous monitoring for security threats and anomalies</li>
                <li><strong>Secure Infrastructure:</strong> Hosted on Google Cloud Platform with enterprise-grade security</li>
              </ul>
              <p className="text-gray-600 leading-relaxed">
                However, no method of transmission over the internet is 100% secure. While we strive to protect your 
                data, we cannot guarantee absolute security.
              </p>
            </section>

            <section className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <CreditCard className="w-6 h-6 text-primary-green" />
                <h2 className="text-2xl font-bold text-gray-900">5. Payment Information</h2>
              </div>
              <p className="text-gray-600 leading-relaxed mb-4">
                All payment processing is handled by Stripe, a PCI-DSS compliant payment processor. We do not store 
                your credit card numbers or payment details on our servers. Stripe collects and processes:
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4 mb-4">
                <li>Credit/debit card information</li>
                <li>Billing address</li>
                <li>Payment history</li>
              </ul>
              <p className="text-gray-600 leading-relaxed">
                For more information on how Stripe handles your payment data, please review{' '}
                <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" 
                   className="text-primary-green hover:underline">
                  Stripe's Privacy Policy
                </a>.
              </p>
            </section>

            <section className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <Mail className="w-6 h-6 text-primary-green" />
                <h2 className="text-2xl font-bold text-gray-900">6. Email Communications</h2>
              </div>
              <p className="text-gray-600 leading-relaxed mb-4">
                We send the following types of emails:
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4 mb-4">
                <li><strong>Expiration Notifications:</strong> Reminders about items expiring in 2 days (essential service emails)</li>
                <li><strong>Household Invitations:</strong> Invites to join a household</li>
                <li><strong>Account Updates:</strong> Changes to your account or subscription status</li>
                <li><strong>Security Alerts:</strong> Notifications about security-related events</li>
              </ul>
              <p className="text-gray-600 leading-relaxed">
                You can adjust notification preferences in your account settings, but some essential emails 
                (such as security alerts) cannot be disabled.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Your Rights and Choices</h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                You have the following rights regarding your personal information:
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">7.1 Access and Portability</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                You can access, view, and export your data at any time through the FridgeLife dashboard and analytics pages.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">7.2 Correction</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                You can update your account information and food inventory data directly in the app.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">7.3 Deletion</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                You can delete your account and all associated data by contacting us at fridgelife@yahoo.com. 
                Upon account deletion:
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4 mb-4">
                <li>Your user account will be permanently deleted</li>
                <li>All your personal data will be removed from our systems within 30 days</li>
                <li>Shared household data will remain accessible to other household members</li>
                <li>Some data may be retained for legal compliance (e.g., payment records for tax purposes)</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">7.4 Opt-Out of Cookies</h3>
              <p className="text-gray-600 leading-relaxed">
                You can disable cookies through your browser settings, but this may affect the functionality 
                of our service.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Data Retention</h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                We retain your information for as long as necessary to provide our service and fulfill the purposes 
                described in this Privacy Policy. Specifically:
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
                <li><strong>Active Accounts:</strong> Data is retained while your account is active</li>
                <li><strong>Inactive Accounts:</strong> After 12 months of inactivity, we may delete your account and data</li>
                <li><strong>After Account Deletion:</strong> Most data is deleted within 30 days, except where required by law</li>
                <li><strong>Payment Records:</strong> Retained for 7 years for tax and accounting purposes</li>
                <li><strong>Log Data:</strong> Automatically deleted after 90 days</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Children's Privacy</h2>
              <p className="text-gray-600 leading-relaxed">
                FridgeLife is not intended for children under 13. We do not knowingly collect personal information 
                from children under 13. If you believe we have collected information from a child under 13, please 
                contact us immediately at fridgelife@yahoo.com, and we will delete such information.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">10. International Data Transfers</h2>
              <p className="text-gray-600 leading-relaxed">
                Your information may be transferred to and processed in countries other than your country of 
                residence. These countries may have different data protection laws. Our servers are located in 
                the United States (Google Cloud Platform), and by using FridgeLife, you consent to the transfer 
                of your information to the United States.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">11. California Privacy Rights</h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                If you are a California resident, you have additional rights under the California Consumer Privacy 
                Act (CCPA):
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
                <li>Right to know what personal information we collect and how it's used</li>
                <li>Right to delete your personal information</li>
                <li>Right to opt-out of the sale of personal information (we do not sell personal information)</li>
                <li>Right to non-discrimination for exercising your privacy rights</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Third-Party Links</h2>
              <p className="text-gray-600 leading-relaxed">
                FridgeLife may contain links to third-party websites or services (such as Open Food Facts). We are 
                not responsible for the privacy practices of these third parties. We encourage you to review their 
                privacy policies before providing any personal information.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">13. Changes to This Privacy Policy</h2>
              <p className="text-gray-600 leading-relaxed">
                We may update this Privacy Policy from time to time. If we make material changes, we will notify 
                you by email or through a prominent notice in the app. The "Last Updated" date at the top of this 
                policy indicates when it was last revised. Your continued use of FridgeLife after changes are 
                posted constitutes acceptance of the updated policy.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">14. Contact Us</h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                If you have questions, concerns, or requests regarding this Privacy Policy or your personal 
                information, please contact us at:
              </p>
              <div className="bg-primary-mint/50 rounded-lg p-6">
                <p className="text-gray-700 font-medium mb-1">FridgeLife Privacy Team</p>
                <p className="text-gray-600 mb-1">Email: fridgelife@yahoo.com</p>
              </div>
            </section>

            <div className="bg-gradient-to-r from-primary-green/10 to-primary-mint/50 rounded-lg p-6 mt-8">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Your Privacy Matters to Us</h3>
              <p className="text-gray-700 leading-relaxed">
                At FridgeLife, we're committed to protecting your privacy and being transparent about how we use 
                your data. We believe in giving you control over your information and building trust through 
                responsible data practices. Thank you for trusting us with your food inventory management.
              </p>
            </div>
          </div>
        </div>
      </div>

      <footer className="bg-white border-t border-gray-200 py-8 mt-16">
        <div className="container mx-auto px-4 text-center">
          <div className="flex flex-wrap justify-center gap-4 mb-4">
            <Link to="/terms" className="text-primary-green hover:text-primary-green/80 font-medium text-sm">
              Terms of Service
            </Link>
            <span className="text-gray-300">•</span>
            <Link to="/privacy" className="text-primary-green hover:text-primary-green/80 font-medium text-sm">
              Privacy Policy
            </Link>
          </div>
          <p className="text-gray-600 text-sm">Built with 💚 by FridgeLife • Helping reduce food waste, one fridge at a time</p>
        </div>
      </footer>
    </div>
  );
};
